import React, { useEffect } from 'react';
import { Container, Title, Text, List, ThemeIcon, Card, createStyles, Anchor, Group, Stack, Badge } from '@mantine/core';
import { IconCheck, IconShieldCheck, IconLock, IconGavel } from '@tabler/icons-react';
import { Link } from 'react-router-dom';

const useStyles = createStyles((theme) => ({
  wrapper: {
    paddingTop: 80,
    paddingBottom: 80,
    minHeight: '100vh',
    backgroundColor: '#050505',
    backgroundImage: 'radial-gradient(circle at center, #1a1a1a 0%, #000000 100%)',
    color: theme.colors.gray[3],
    [theme.fn.smallerThan('sm')]: {
      paddingTop: 60,
      paddingBottom: 40,
    },
  },

  title: {
    fontSize: 34,
    fontWeight: 800,
    marginBottom: theme.spacing.xl,
    color: theme.white,
    textAlign: 'center',
    [theme.fn.smallerThan('sm')]: {
      fontSize: 28,
      marginBottom: theme.spacing.md,
    },
  },

  sectionTitle: {
    fontSize: 22,
    fontWeight: 700,
    marginTop: theme.spacing.xl * 1.5,
    marginBottom: theme.spacing.md,
    color: theme.colors.cyan[4],
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    marginTop: '35px',
    [theme.fn.smallerThan('sm')]: {
      fontSize: 18,
      marginTop: theme.spacing.lg,
    },
  },

  content: {
    fontSize: 16,
    lineHeight: 1.6,
    color: theme.colors.gray[4],
    [theme.fn.smallerThan('sm')]: {
      fontSize: 14,
    },
  },

  card: {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.05)',
    padding: theme.spacing.xl,
    borderRadius: theme.radius.lg,
    [theme.fn.smallerThan('sm')]: {
      padding: theme.spacing.md,
    },
  },

  list: {
    marginTop: theme.spacing.sm,
    marginBottom: theme.spacing.sm,
    color: theme.colors.gray[3],
  }
}));

export default function Policy() {
  const { classes } = useStyles();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={classes.wrapper}>
      <Container size="md">
        <Card className={classes.card}>
          <Title className={classes.title}>Privacy Policy</Title>

          <Group position="center" mb="xl">
            <Badge size="lg" color="cyan">Effective Date: 06-02-2026</Badge>
            <Badge size="lg" variant="outline" color="gray">Digital Personal Data Protection Act, 2023</Badge>
          </Group>

          <Text className={classes.content}>
            At <strong>CDP3D</strong>, accessible from <Anchor href="https://cdp3d.com" target="_blank" color="cyan">https://cdp3d.com</Anchor>, we value your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, store, and protect information when you use our website and services.
            <br /><br />
            By using our website, you consent to the practices described in this Privacy Policy.
          </Text>

          {/* 1. Information We Collect */}
          <Title className={classes.sectionTitle}>
            <ThemeIcon color="cyan" variant="light" radius="xl"><IconCheck size="1rem" /></ThemeIcon>
            1. Information We Collect
          </Title>
          <Text className={classes.content}>We may collect the following personal and business-related information:</Text>

          <Text weight={700} color="white" mt="sm">a) Information You Provide Directly</Text>
          <List spacing="xs" size="sm" center className={classes.list} icon={<ThemeIcon color="cyan" variant="filled" size={16} radius="xl"><IconCheck size="0.9rem" /></ThemeIcon>}>
            <List.Item>Full name</List.Item>
            <List.Item>Email address</List.Item>
            <List.Item>Phone number</List.Item>
            <List.Item>Billing and shipping address</List.Item>
            <List.Item>Uploaded 3D design files (STL, STEP, CAD, etc.)</List.Item>
            <List.Item>Order details and specifications</List.Item>
            <List.Item>Communications with us (emails, messages)</List.Item>
          </List>

          <Text weight={700} color="white" mt="sm">b) Payment Information</Text>
          <Text className={classes.content}>
            Payments made on CDP3D are processed through secure third-party payment gateways.
            We do not store your credit/debit card details, UPI information, or banking credentials.
          </Text>

          <Text weight={700} color="white" mt="sm">c) Automatically Collected Information</Text>
          <List spacing="xs" size="sm" center className={classes.list} icon={<ThemeIcon color="cyan" variant="filled" size={16} radius="xl"><IconCheck size="0.9rem" /></ThemeIcon>}>
            <List.Item>IP address</List.Item>
            <List.Item>Browser type & Device information</List.Item>
            <List.Item>Date and time of access</List.Item>
            <List.Item>Pages visited and referring URLs</List.Item>
            <List.Item>Cookies and tracking technologies</List.Item>
          </List>

          {/* 2. Purpose of Data Collection */}
          <Title className={classes.sectionTitle}>
            <ThemeIcon color="cyan" variant="light" radius="xl"><IconCheck size="1rem" /></ThemeIcon>
            2. Purpose of Data Collection
          </Title>
          <Text className={classes.content}>We use your information to:</Text>
          <List spacing="xs" size="sm" center className={classes.list}>
            <List.Item>Process and fulfill 3D printing orders</List.Item>
            <List.Item>Manufacture, quality-check, and deliver products</List.Item>
            <List.Item>Process payments and invoices</List.Item>
            <List.Item>Communicate order updates</List.Item>
            <List.Item>Provide customer support & improve UX</List.Item>
            <List.Item>Comply with legal requirements</List.Item>
          </List>

          {/* 3. 3D File Uploads */}
          <Title className={classes.sectionTitle}>
            <ThemeIcon color="pink" variant="light" radius="xl"><IconLock size="1rem" /></ThemeIcon>
            3. 3D File Uploads & Intellectual Property
          </Title>
          <Text className={classes.content}>
            Customers retain full ownership and intellectual property rights over the 3D design files they upload.
          </Text>
          <List spacing="xs" size="sm" mt="xs" className={classes.list}>
            <List.Item>Uploaded files are used <strong>only</strong> for manufacturing requested products.</List.Item>
            <List.Item>We do <strong>not</strong> sell, share, distribute, or reuse customer design files.</List.Item>
            <List.Item>Files are stored securely and retained only as long as necessary.</List.Item>
          </List>

          {/* 4. Data Sharing */}
          <Title className={classes.sectionTitle}>4. Data Sharing and Disclosure</Title>
          <Text className={classes.content}>
            We do not sell or rent your personal data. We may share information only with:
          </Text>
          <List spacing="xs" size="sm" mt="xs" className={classes.list}>
            <List.Item>Payment gateway providers (for processing)</List.Item>
            <List.Item>Shipping partners (for delivery)</List.Item>
            <List.Item>IT/Hosting providers (for operations)</List.Item>
            <List.Item>Government authorities (if required by law)</List.Item>
          </List>

          {/* 5. Data Retention */}
          <Title className={classes.sectionTitle}>5. Data Retention</Title>
          <Text className={classes.content}>
            We retain data only as long as necessary for fulfillment, legal, or accounting compliance. Uploaded 3D files may be deleted after order completion unless needed for warranty/reorders.
          </Text>

          {/* 6. Data Security */}
          <Title className={classes.sectionTitle}>
            <ThemeIcon color="green" variant="light" radius="xl"><IconShieldCheck size="1rem" /></ThemeIcon>
            6. Data Security
          </Title>
          <Text className={classes.content}>
            We use secure servers, access controls, and encrypted connections (HTTPS). However, no online transmission is 100% secure.
          </Text>

          {/* 7. Cookies */}
          <Title className={classes.sectionTitle}>7. Cookies</Title>
          <Text className={classes.content}>
            We use cookies to improve performance, analyze traffic, and enhance UX. You may disable them in browser settings, though some features may break.
          </Text>

          {/* 8. Your Rights */}
          <Title className={classes.sectionTitle}>
            <ThemeIcon color="orange" variant="light" radius="xl"><IconGavel size="1rem" /></ThemeIcon>
            8. Your Rights (Under Indian Law)
          </Title>
          <Text className={classes.content}>
            You have the right to access, correct, delete your data, or withdraw consent.
            To exercise these rights, please <Anchor component={Link} to="/contact-us" color="cyan" weight={700}>contact us</Anchor>.
          </Text>

          {/* 9. Children's Privacy */}
          <Title className={classes.sectionTitle}>9. Children’s Privacy</Title>
          <Text className={classes.content}>
            Our services are not intended for children under 18. We do not knowingly collect personal data from minors.
          </Text>

          {/* 10. Third-Party Websites */}
          <Title className={classes.sectionTitle}>10. Third-Party Websites</Title>
          <Text className={classes.content}>
            We are not responsible for the privacy practices or content of external links.
          </Text>

          {/* 11. Changes */}
          <Title className={classes.sectionTitle}>11. Changes to This Policy</Title>
          <Text className={classes.content}>
            Updates will be posted on this page with a new effective date.
          </Text>

          {/* 12. Contact */}
          <Title className={classes.sectionTitle}>12. Contact & Grievance Redressal</Title>
          <Stack spacing={4}>
            <Text className={classes.content}><strong>Business Name:</strong> CDP3D</Text>
            <Text className={classes.content}><strong>Email:</strong> cdpthreed@gmail.com</Text>
            <Text className={classes.content}><strong>Address:</strong> 184-A, Gali No. 3, Padam Nagar, Delhi-110007</Text>
            <Text className={classes.content} mt="sm">We will respond to grievances within a reasonable timeframe as required by law.</Text>
          </Stack>

          <Title className={classes.sectionTitle}>13. Consent</Title>
          <Text className={classes.content}>
            By using our website, you acknowledge that you have read and understood this Privacy Policy and agree to its terms.
          </Text>

        </Card>
      </Container>
    </div>
  );
}
